import { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isUserHasKeys } from '../../GlobalFunctions';
import { LockedContentMessage } from '../../LessonPlan/LockedContentMessage';
import { updateField } from '../../UserManagement';
import styles from '../../SCSS/Header.module.scss';
import { History, Location } from 'history';
import { AppDispatch, RootState } from '../../ApplicationState';

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserProfile: (field: string, value: unknown) => dispatch(updateField({ field, value })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  location: Location;
  history: History;
  isAuthenticated: boolean;
};

type State = {
  isShowLockedMessage: boolean;
  selected?: number;
};

class Subheader extends Component<Props, State> {
  onHideLockedMessage = () => {
    this.setState({
      isShowLockedMessage: false,
    });
  };

  /* eslint-disable-next-line complexity */

  constructor(props) {
    super(props);

    const baseMenuOptions = [
      {
        title: 'Library',
        route: '/library',
      },
      {
        title: 'Students + Parents',
        route: '/students',
      },
    ];

    // Only add Check-In Tool if user has the correct license key
    const hasCheckInAccess = isUserHasKeys(['KEY_TEACHER_CHECK-IN']);
    if (hasCheckInAccess) {
      baseMenuOptions.push({
        title: 'Check-In Tool',
        route: '/teacherLedCheckIn',
      });
    }

    baseMenuOptions.push(
      {
        title: 'Classroom Settings',
        route: '/classroom',
      },
      {
        title: 'Reports',
        route: '/reports',
      },
    );

    this.menuOptions = baseMenuOptions;

    this.state = {
      selected: undefined,
      isShowLockedMessage: false,
    };
  }

  componentDidMount() {
    this.computeSelectedBasedOnRoute();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.computeSelectedBasedOnRoute();
    }
  }

  computeSelectedBasedOnRoute() {
    let newSelectedIndex;
    this.menuOptions.forEach((option) => {
      if (this.props.location.pathname.includes(option.route.toLowerCase())) {
        newSelectedIndex = this.mapTitleToIndex(option.title);
      }
    });
    this.setState({
      selected: newSelectedIndex,
    });
  }

  selectItem(itemIndex) {
    const saveSelected = this.state.selected;
    this.setState({
      selected: itemIndex,
    });

    if (this.menuOptions[itemIndex].route) {
      this.props.history.push(this.menuOptions[itemIndex].route.toLowerCase());
    }
  }

  mapTitleToIndex(title: string) {
    // Find the index dynamically based on the actual menuOptions array
    return this.menuOptions.findIndex((option) => option.title === title);
  }

  render() {
    // console.debug(`%cSubheader render`, 'background: blue; color: yellow');

    if (!this.props.userProfile) {
      return <div className={styles.Subheader} />;
    }

    return (
      <div className={styles.Subheader}>
        <div className={styles.subheaderContainer}>
          {this.menuOptions.map((option, index) => {
            const currentlySelectedTab = this.state.selected;
            return (
              <div
                key={index}
                className={`${styles.subheaderSection} ${currentlySelectedTab === index ? styles.selected : ''}`}
                onClick={() => this.selectItem(index)}
              >
                {option.title === 'Check-In Tool' ? (
                  <OverlayTrigger
                    placement='bottom'
                    trigger={['hover', 'focus']}
                    overlay={
                      <Tooltip id='beta-badge-tooltip' style={{ fontSize: '1.3rem' }}>
                        Conduct student check-ins directly from your device—no student login required. Results appear in
                        the Moodboard report.
                      </Tooltip>
                    }
                  >
                    <div className={styles.betaBadgeContainer}>
                      <span>{option.title}</span>
                      <Badge className={styles.betaBadge} bg='warning' text='dark'>
                        Beta
                      </Badge>
                    </div>
                  </OverlayTrigger>
                ) : (
                  option.title
                )}
              </div>
            );
          })}
        </div>
        <LockedContentMessage isShow={this.state.isShowLockedMessage} onHide={this.onHideLockedMessage} feature />
      </div>
    );
  }
}

export default connector(Subheader);
